import { cloneDeep } from 'lodash-es'
import dayjs from 'dayjs'
import type {
  IImmunizationDose,
  IImmunizationInfo,
  IImmunizationRecord,
  IStudent,
} from 'src/interfaces/family'
import type {
  IImmunizationDosePreference,
  IImmunizationRecordPreference,
} from 'src/interfaces/custom'
import {
  immunizationInfoTemplate,
  immunizationRecordTemplate,
} from 'src/templates/family'
import { getImmunizationRecordStatus } from 'src/utils/immunizationStatus'

export type SetupImmunizationRecord =
  | string
  | IImmunizationRecordPreference
  | {
      id?: string
      name?: string
      value?: string
      dueAgesInMonths?: number[]
      doses?: IImmunizationDosePreference[]
    }

const immunizationAttachmentMutationQueues = new Map<
  string,
  Promise<unknown>
>()

export function enqueueImmunizationAttachmentMutation<T>(
  familyId: string,
  operation: () => Promise<T>,
) {
  const previous =
    immunizationAttachmentMutationQueues.get(familyId) ?? Promise.resolve()
  const result = previous.then(operation, operation)
  const queuedResult = result.catch(() => undefined)
  immunizationAttachmentMutationQueues.set(familyId, queuedResult)
  void queuedResult.finally(() => {
    if (immunizationAttachmentMutationQueues.get(familyId) === queuedResult) {
      immunizationAttachmentMutationQueues.delete(familyId)
    }
  })
  return result
}

function getRecordKey(name?: string) {
  return name?.trim().toLocaleLowerCase()
}

export function immunizationRecordsMatch(
  left?: Pick<IImmunizationRecord, 'vaccineId' | 'name'>,
  right?: Pick<IImmunizationRecord, 'vaccineId' | 'name'>,
) {
  if (!left || !right) return false
  if (left.vaccineId && right.vaccineId) {
    return left.vaccineId === right.vaccineId
  }
  const leftName = getRecordKey(left.name)
  return Boolean(leftName && leftName === getRecordKey(right.name))
}

export function immunizationDosesMatch(
  left?: Pick<IImmunizationDose, 'doseId' | 'name'>,
  right?: Pick<IImmunizationDose, 'doseId' | 'name'>,
) {
  if (!left || !right) return false
  if (left.doseId && right.doseId) return left.doseId === right.doseId
  const leftName = getRecordKey(left.name)
  return Boolean(leftName && leftName === getRecordKey(right.name))
}

function getRecordSessionKey(record: IImmunizationRecord) {
  return record.vaccineId
    ? `id:${record.vaccineId}`
    : `name:${getRecordKey(record.name)}`
}

export function preserveImmunizationRecordSessionOrder(
  records: IImmunizationRecord[],
  sessionOrder: string[],
) {
  const occurrenceByKey = new Map<string, number>()
  const keyedRecords = records.map((record) => {
    const baseKey = getRecordSessionKey(record)
    const occurrence = occurrenceByKey.get(baseKey) ?? 0
    occurrenceByKey.set(baseKey, occurrence + 1)
    return { record, key: `${baseKey}:${occurrence}` }
  })

  for (const { key } of keyedRecords) {
    if (!sessionOrder.includes(key)) sessionOrder.push(key)
  }

  const orderByKey = new Map(
    sessionOrder.map((key, index) => [key, index] as const),
  )
  return keyedRecords
    .sort(
      (left, right) =>
        (orderByKey.get(left.key) ?? Number.MAX_SAFE_INTEGER) -
        (orderByKey.get(right.key) ?? Number.MAX_SAFE_INTEGER),
    )
    .map(({ record }) => record)
}

function normalizeSetupRecord(
  record: SetupImmunizationRecord,
): IImmunizationRecordPreference | undefined {
  if (typeof record === 'string') {
    const name = record.trim()
    return name ? { id: '', name } : undefined
  }

  const name = (
    record.name ??
    ('value' in record ? record.value : '') ??
    ''
  ).trim()
  if (!name) return undefined

  return {
    id: record.id ?? '',
    name,
    dueAgesInMonths:
      'dueAgesInMonths' in record
        ? normalizeDueAgesInMonths(record.dueAgesInMonths)
        : undefined,
    doses: normalizeSetupDoses(
      'doses' in record ? record.doses : undefined,
      'dueAgesInMonths' in record ? record.dueAgesInMonths : undefined,
    ),
  }
}

function normalizeSetupDoses(
  doses?: IImmunizationDosePreference[],
  legacyAges?: number[],
) {
  if (doses?.length) {
    return doses
      .filter(
        (dose) =>
          dose.name?.trim() &&
          Number.isInteger(dose.dueAgeInMonths) &&
          dose.dueAgeInMonths >= 0,
      )
      .map((dose) => ({
        id: dose.id,
        name: dose.name.trim(),
        dueAgeInMonths: dose.dueAgeInMonths,
      }))
      .sort((left, right) => left.dueAgeInMonths - right.dueAgeInMonths)
  }

  return (normalizeDueAgesInMonths(legacyAges) ?? []).map((age, index) => ({
    name: `Dose ${index + 1}`,
    dueAgeInMonths: age,
  }))
}

function mergeScheduledDoses(
  record: IImmunizationRecord,
  setupRecord: IImmunizationRecordPreference,
  dateOfBirth?: string | Date,
) {
  const birthDate = dayjs(dateOfBirth)
  const existingDoses = record.doses ?? []
  record.doses = (setupRecord.doses ?? []).map((setupDose, index) => {
    const existingDose =
      existingDoses.find((dose) =>
        immunizationDosesMatch(dose, {
          doseId: setupDose.id,
          name: setupDose.name,
        }),
      ) ??
      (!setupDose.id || !existingDoses[index]?.doseId
        ? existingDoses[index]
        : undefined)

    return {
      doseId: setupDose.id,
      name: setupDose.name,
      dueAgeInMonths: setupDose.dueAgeInMonths,
      dueDate: birthDate.isValid()
        ? birthDate.add(setupDose.dueAgeInMonths, 'month').format('YYYY-MM-DD')
        : undefined,
      dateGiven: existingDose?.dateGiven,
      fileUrls: existingDose?.fileUrls ?? [],
    }
  })

  syncLegacyDatesFromDoses(record)
}

export function syncLegacyDatesFromDoses(record: IImmunizationRecord) {
  if (!record.doses?.length) return
  const givenDates = record.doses
    .map((dose) => dose.dateGiven)
    .filter((date): date is string => Boolean(date))
    .sort()
  record.lastDoseDate = givenDates.at(-1)
  record.nextDueDate =
    record.doses.find((dose) => !dose.dateGiven)?.dueDate ?? 'N/A'
}

export function isDoseDateInSequence(
  record: IImmunizationRecord,
  dose: IImmunizationDose,
  dateGiven: string | Date,
) {
  const doses = record.doses ?? []
  const doseIndex = record.doses?.indexOf(dose) ?? -1
  const selectedDate = dayjs(dateGiven)
  if (doseIndex < 0 || !selectedDate.isValid()) return true

  const previousDates = doses
    .slice(0, doseIndex)
    .map((item) => item.dateGiven)
    .filter((date): date is string => Boolean(date))
  const laterDates = doses
    .slice(doseIndex + 1)
    .map((item) => item.dateGiven)
    .filter((date): date is string => Boolean(date))

  return (
    previousDates.every(
      (date) => !selectedDate.isBefore(dayjs(date), 'day'),
    ) &&
    laterDates.every((date) => !selectedDate.isAfter(dayjs(date), 'day'))
  )
}

export function normalizeDueAgesInMonths(ages?: number[]) {
  if (!ages) return undefined

  return Array.from(
    new Set(
      ages.filter(
        (age) => Number.isInteger(age) && Number.isFinite(age) && age >= 0,
      ),
    ),
  ).sort((left, right) => left - right)
}

export function calculateScheduledNextDueDate(
  record: IImmunizationRecord,
  setupRecord: IImmunizationRecordPreference | undefined,
  dateOfBirth?: string | Date,
) {
  const dueAgesInMonths = normalizeDueAgesInMonths(setupRecord?.dueAgesInMonths)
  if (!dateOfBirth || !dueAgesInMonths?.length) return record.nextDueDate
  if (record.exemptionReason) return undefined

  const birthDate = dayjs(dateOfBirth)
  if (!birthDate.isValid()) return record.nextDueDate

  const milestoneDates = dueAgesInMonths.map((age) =>
    birthDate.add(age, 'month').format('YYYY-MM-DD'),
  )
  if (!record.lastDoseDate) return milestoneDates[0]

  const lastDoseDate = dayjs(record.lastDoseDate)
  if (!lastDoseDate.isValid()) return milestoneDates[0]

  return (
    milestoneDates.find((milestone) =>
      dayjs(milestone).isAfter(lastDoseDate, 'day'),
    ) ?? 'N/A'
  )
}

export function findSetupImmunizationRecord(
  record: IImmunizationRecord,
  setupRecords: SetupImmunizationRecord[] | undefined,
) {
  return (setupRecords ?? [])
    .map(normalizeSetupRecord)
    .filter(Boolean)
    .find(
      (setupRecord) => immunizationRecordsMatch(record, {
        vaccineId: setupRecord!.id,
        name: setupRecord!.name,
      }),
    )
}

export function updateScheduledNextDueDate(
  record: IImmunizationRecord,
  setupRecords: SetupImmunizationRecord[] | undefined,
  dateOfBirth?: string | Date,
) {
  const setupRecord = findSetupImmunizationRecord(record, setupRecords)
  if (setupRecord && !setupRecord.doses?.length) {
    record.nextDueDate = undefined
    return
  }
  record.nextDueDate = calculateScheduledNextDueDate(
    record,
    setupRecord,
    dateOfBirth,
  )
}

export function hasImmunizationRecordFiles(record: IImmunizationRecord) {
  return Boolean(
    record.recordFileUrls?.length ||
    record.exemptionFileUrls?.length ||
    record.doses?.some((dose) => dose.fileUrls?.length),
  )
}

export function isRemovedSetupImmunizationRecord(
  record: IImmunizationRecord,
  setupRecords: SetupImmunizationRecord[] | undefined,
) {
  return (
    Boolean(setupRecords?.length) &&
    !findSetupImmunizationRecord(record, setupRecords)
  )
}

export function getConfiguredImmunizationRecords(
  records: IImmunizationRecord[] | undefined,
  setupRecords: SetupImmunizationRecord[] | undefined,
) {
  if (!setupRecords?.length) return records ?? []
  return (records ?? []).filter((record) =>
    findSetupImmunizationRecord(record, setupRecords),
  )
}

function orderImmunizationRecordsForReview(
  records: IImmunizationRecord[],
  setupRecords: SetupImmunizationRecord[] | undefined,
) {
  if (!setupRecords?.length) return records

  return records
    .map((record, index) => {
      const status = getImmunizationRecordStatus(record)
      const setupIndex = setupRecords.findIndex((setupRecord) =>
        findSetupImmunizationRecord(record, [setupRecord]),
      )

      return {
        record,
        order: setupIndex >= 0 ? setupIndex : setupRecords.length + index,
        priority: isRemovedSetupImmunizationRecord(record, setupRecords)
          ? 0
          : status === 'Missing Info'
            ? 1
            : status === 'Overdue'
              ? 2
              : 3,
      }
    })
    .sort(
      (left, right) =>
        left.priority - right.priority || left.order - right.order,
    )
    .map(({ record }) => record)
}

export function mergeSetupImmunizationRecords(
  immunizationInfo: IImmunizationInfo | undefined,
  setupRecords: SetupImmunizationRecord[] | undefined,
  dateOfBirth?: string | Date,
): IImmunizationInfo {
  const mergedInfo = cloneDeep(immunizationInfo ?? immunizationInfoTemplate)
  const existingRecords = mergedInfo.immunizationRecord ?? []
  const normalizedSetupRecords = (setupRecords ?? [])
    .map(normalizeSetupRecord)
    .filter(Boolean) as IImmunizationRecordPreference[]

  // Once a centre has a configured vaccine list, stale records without any
  // evidence can be dropped on the student's next save. Records that still own
  // files remain visible so those files can be reassigned or deliberately
  // removed first.
  const records = normalizedSetupRecords.length
    ? existingRecords.filter(
        (record) =>
          Boolean(findSetupImmunizationRecord(record, normalizedSetupRecords)) ||
          hasImmunizationRecordFiles(record),
      )
    : [...existingRecords]

  const missingSetupRecords = normalizedSetupRecords
    .filter((setupRecord) => {
      const matchingRecord = records.find((record) =>
        immunizationRecordsMatch(record, {
          vaccineId: setupRecord.id,
          name: setupRecord.name,
        }),
      )

      if (!matchingRecord) return true

      if (setupRecord.id) {
        matchingRecord.vaccineId = setupRecord.id
      }
      matchingRecord.name = setupRecord.name
      if (setupRecord.doses?.length) {
        mergeScheduledDoses(matchingRecord, setupRecord, dateOfBirth)
      } else {
        matchingRecord.nextDueDate = undefined
      }
      return false
    })
    .map<IImmunizationRecord>((setupRecord) => {
      const record = {
        ...cloneDeep(immunizationRecordTemplate),
        vaccineId: setupRecord.id || undefined,
        name: setupRecord.name,
      }
      if (setupRecord.doses?.length) {
        mergeScheduledDoses(record, setupRecord, dateOfBirth)
      } else {
        record.nextDueDate = undefined
      }
      return record
    })

  return {
    ...mergedInfo,
    immunizationRecord: orderImmunizationRecordsForReview(
      [...records, ...missingSetupRecords],
      normalizedSetupRecords,
    ),
  }
}

export function mergeSetupImmunizationRecordsForStudent<
  TStudent extends Pick<IStudent, 'immunizationInfo' | 'dateOfBirth'>,
>(
  student: TStudent | undefined,
  setupRecords: SetupImmunizationRecord[] | undefined,
) {
  if (!student) return student

  return {
    ...cloneDeep(student),
    immunizationInfo: mergeSetupImmunizationRecords(
      student.immunizationInfo,
      setupRecords,
      student.dateOfBirth,
    ),
  }
}

export function applySetupImmunizationRecordsToStudent(
  student: IStudent,
  setupRecords: SetupImmunizationRecord[] | undefined,
) {
  student.immunizationInfo = mergeSetupImmunizationRecords(
    student.immunizationInfo,
    setupRecords,
    student.dateOfBirth,
  )
}

const previousNextDueDateByRecord = new WeakMap<
  IImmunizationRecord,
  string | undefined
>()

export function isNextDueDateNotApplicable(record: IImmunizationRecord) {
  return record.nextDueDate === 'N/A'
}

export function getApplicableNextDueDate(record: IImmunizationRecord) {
  return isNextDueDateNotApplicable(record) ? undefined : record.nextDueDate
}

export function updateApplicableNextDueDate(
  record: IImmunizationRecord,
  value: string | undefined,
) {
  record.nextDueDate = value
  if (value) {
    previousNextDueDateByRecord.set(record, value)
  }
}

export function setNextDueDateNotApplicable(
  record: IImmunizationRecord,
  isNotApplicable: boolean,
) {
  if (isNotApplicable) {
    if (record.nextDueDate && record.nextDueDate !== 'N/A') {
      previousNextDueDateByRecord.set(record, record.nextDueDate)
    }

    record.nextDueDate = 'N/A'
    return
  }

  record.nextDueDate = previousNextDueDateByRecord.get(record)
}
